import { AppBar, Box, Button, Container, IconButton, Menu, MenuItem, Toolbar, Typography, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const pages = [
  { name: 'Home', path: '/' },
  { name: 'How It Works', path: '/how-it-works' },
  { name: 'About', path: '/about' },
  { name: 'Reviews', path: '/reviews' },
  { name: 'FAQ', path: '/faq' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const theme = useTheme();
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar 
      position="fixed"
      elevation={0}
      sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        boxShadow: 'none',
        backdropFilter: 'blur(10px)',
        borderRadius: 0,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          {/* Logo for larger screens */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              textDecoration: 'none',
              color: 'white',
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="VYVeterans Logo"
              sx={{
                height: 40,
                width: 'auto',
                mr: 2,
                filter: 'brightness(1)',
              }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                //change the font
                fontFamily: "sans-serif",
                //all capital
                textTransform: 'uppercase',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: 'white',
              }}
            >
              Vehicles For Young Veterans
            </Typography>
          </Box>

          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{ color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiPaper-root': {
                  background: theme.palette.primary.main,
                },
              }}
            >
              {pages.map((page) => (
                <MenuItem 
                  key={page.name} 
                  onClick={handleCloseNavMenu}
                  component={Link}
                  to={page.path}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <Typography textAlign="center">{page.name}</Typography>
                </MenuItem>
              ))}
              <MenuItem 
                onClick={handleCloseNavMenu}
                component={Link}
                to="/donate"
                sx={{
                  color: 'white',
                  backgroundColor: theme.palette.secondary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                  },
                  mt: 1,
                  borderRadius: 1,
                }}
              >
                <Typography textAlign="center">Donate Now</Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Logo for mobile */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              alignItems: 'center',
              textDecoration: 'none',
              color: 'white',
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="VYVeterans Logo"
              sx={{
                height: 32,
                width: 'auto',
                mr: 1,
                filter: 'brightness(1)',
              }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                //change the font
                fontFamily: 'Raleway, sans-serif',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: 'white',
              }}
            >
              VFYV.org
            </Typography>
          </Box>

          {/* Desktop menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', alignItems: 'center' }}>
            {pages.map((page) => {
              const isActive = location.pathname === page.path || 
                (page.path === '/' && location.pathname === '');
              
              return (
                <Button
                  key={page.name}
                  component={Link}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={{
                    my: 2,
                    mx: 1,
                    color: 'white',
                    display: 'block',
                    position: 'relative',
                    fontSize: '1rem',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '&::after': {
                        width: '100%',
                      },
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: isActive ? '100%' : '0%',
                      height: '2px',
                      backgroundColor: 'white',
                      transition: 'width 0.3s ease',
                    },
                  }}
                >
                  {page.name}
                </Button>
              );
            })}
            <Button
              component={Link}
              to="/donate"
              variant="contained"
              color="secondary"
              sx={{
                ml: 2,
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.dark,
                },
              }}
            >
              Donate Now
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
